"use client";

import { trackSeoLoggedOutBrandC2TabClick } from "@faire/web-api/events/seo/click/loggedOutBrandC2Tab";
import { trackSeoLoggedOutBrandTabScroll } from "@faire/web-api/events/seo/scroll/loggedOutBrandTab";
import { IFilterOption } from "@faire/web-api/indigofair/data/IFilterOption";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { MIN_WIDTH_BREAKPOINTS } from "@faire/web/common/media";
import { useIsSSR } from "@faire/web/common/server-side-rendering/isSSR";
import { isNotNull } from "@faire/web/common/typescriptUtils";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { Tab, Tabs } from "@faire/web/slate/Tabs";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { getWindowWidth } from "@faire/web/ui/hooks/useObservableWindow";
import { History } from "history";
import React from "react";
import styled, { css } from "styled-components";

import { useLoggedOutProductFilters } from "./brand-products/useLoggedOutProductFilters";
import { CATEGORY_KEY_PREFIX, ALL_PRODUCTS_KEY } from "./consts";

export const FilterTabs: React.FC<{
  brandToken: string | undefined;
  categoryFilterOptions: Array<IFilterOption> | null;
  scrollIntoViewOnClickMobileElementId?: string;
  locationPush: (location: History.LocationDescriptor) => void;
}> = ({
  brandToken,
  categoryFilterOptions,
  scrollIntoViewOnClickMobileElementId,
  locationPush,
}) => {
  const ssr = useIsSSR();
  const { selectedCategoryFilterKey, setCategoryFilterKey } =
    useLoggedOutProductFilters(locationPush);

  const handleFilterSelected = (categoryFilterKey: string) => {
    trackSeoLoggedOutBrandC2TabClick(brandToken ?? "", categoryFilterKey);
    setCategoryFilterKey(categoryFilterKey);
  };

  const activeTab =
    selectedCategoryFilterKey ?? `${CATEGORY_KEY_PREFIX}${ALL_PRODUCTS_KEY}`;

  if (!categoryFilterOptions?.length) {
    return <Spacer width="5x" />;
  }

  return (
    <StyledTabs
      data-test-id="category-filters"
      $ssr={ssr}
      tabs={categoryFilterOptions
        .map((category): Tab<string> | null => {
          if (category.key && category.display_name) {
            return {
              value: category.key,
              label: category.display_name,
            };
          } else {
            return null;
          }
        })
        .filter(isNotNull)}
      activeTab={activeTab}
      onTabChange={(key) => {
        if (key === activeTab) {
          return;
        }

        handleFilterSelected(key);

        const isMobile = getWindowWidth() < MIN_WIDTH_BREAKPOINTS.TABLET;
        if (isMobile && scrollIntoViewOnClickMobileElementId) {
          getWindow()
            ?.document.getElementById(scrollIntoViewOnClickMobileElementId)
            ?.scrollIntoView();
        }
      }}
      onScroll={(start, end) => {
        trackSeoLoggedOutBrandTabScroll(
          brandToken ?? "",
          start.toString(),
          end.toString(),
          // Determines if the Tabs component has been scroll to its end
          // (with a threshold of 95%).
          end > 0.95 ? "true" : "false"
        );
      }}
      tabAs="h2"
      autoScrollIntoView
    />
  );
};

const StyledTabs = styled(Tabs)<{ $ssr: boolean }>`
  margin-bottom: ${getSpacing("2x")};

  ${(props) =>
    props.$ssr
      ? css`
          button {
            opacity: 0.3;
            cursor: wait;
          }
        `
      : ""}

  ${responsiveMediaQueries.mobile} {
    margin: ${getSpacing("0", "-2x", "2x", "-2x")};
    width: unset;
    position: sticky;
    top: 0;
    z-index: 11;
    background: white;
  }
`;
